/**
 * PlannerCalendar — the calendar, rebuilt from Rennan's Planner
 * artboard.
 *
 * PLANNER-1. He sent a Design canvas and said: *"this calendar is the
 * new UI for calendar. however merge the existing settings into this
 * new calendar."* The artboard is the spec — three columns, a
 * mini-month and an Appearance panel down the left, Day/Week/Month
 * across the top, Reminders down the right — and it is recreated here
 * rather than reinterpreted: its measurements, its type sizes and its
 * tokens, in `planner.css`.
 *
 * WHAT THIS REPLACES, AND WHAT WAS ALREADY THERE
 *
 * Three calendars existed in this repo at once. `CalendarPage` (cream)
 * and `NewCalendar` (dark shell) were both unreachable — `useGoogie()`
 * has returned `true` unconditionally since GOOGIE-15, so the route
 * always landed on `GoogieCalendar`, a month grid with a day list
 * beside it. This takes the route. The other three go in PLANNER-4
 * rather than in the same change that replaced them.
 *
 * What the artboard does NOT have, and what therefore comes from the
 * CAL-PRO work rather than from the design:
 *
 *   · OVERLAPS. The artboard's week and day events are `left: 3px;
 *     right: 3px`, so two shoots at eleven draw on top of each other.
 *     `timeGrid.layoutDay` splits the column instead — transitively
 *     clustered, columns reused as they free.
 *   · DRAG. Move, stretch either edge, or draw a new block on empty
 *     space (`useGridDrag`).
 *   · BOOKABLE HOURS, shaded behind the grid from `availability_rules`.
 *
 * Those live in `../calendar/*` and are mounted here. The artboard's
 * own chrome goes over them in PLANNER-1b.
 *
 * APPEARANCE, BOUND TO ORBIT
 *
 * Rennan asked for the artboard's controls kept but bound to the app:
 * Light and Dark ARE the app's own mode, so the calendar cannot
 * disagree with the shell around it, and the six accents are Orbit's
 * palette. Sand is a third paper over light, which is what it is in
 * the artboard too.
 *
 * The planner is deliberately NOT inside `GoogieScreen`: it is an
 * opaque surface, not a page under the seasonal sky, so it takes
 * neither the sky nor GOOGIE-FROST-1's frosting. A calendar you read
 * for an hour wants paper, not weather. Navigation is unaffected —
 * `RootLayout` is above the route and still provides the rail.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { toast } from 'sonner';
import { useThemeMode } from '@/hooks/useThemeMode';
import { useOrganization } from '@/services/organizations';
import {
  useCalendarEvents,
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  useUpdateCalendarEvent,
  type CalendarEventRow,
} from '@/services/calendarEvents';
import { useAvailabilityRules, useBookingLinks } from '@/services/scheduler';
import SchedulingSettings from '@/components/settings/SchedulingSettings';
import { useCalendarStream } from '@/services/calendarStream';
import EventDetailsModal, { type DetailedCalendarEvent } from '../calendar/EventDetailsModal';
import EventComposer, { type ComposerDraft, type ComposerSave } from '../calendar/EventComposer';
import { TimeGridView } from '../calendar/TimeGridView';
import { AgendaView } from '../calendar/AgendaView';
import { bookableBands, weekDays, type Span } from '../calendar/timeGrid';
import { cn } from '@/lib/utils';
import { useCalendarFilters, type CalendarRow } from './useCalendarFilters';
import { RemindersPanel } from './RemindersPanel';
import type { ReminderRow } from '@/services/reminders';
import './planner.css';

/* ─────────────────────────── settings ─────────────────────────── */

/** The artboard's three papers. Light and Dark are Orbit's own mode. */
const PAPERS = [
  { id: 'light', name: 'Light' },
  { id: 'dark', name: 'Dark' },
  { id: 'sand', name: 'Sand' },
] as const;
type Paper = (typeof PAPERS)[number]['id'];

/**
 * Six accents, Orbit's palette in the artboard's six slots.
 *
 * The swatch is the light-mode value; `planner.css` lifts each one for
 * dark, where a mid blue on #111113 is unreadable.
 */
const ACCENTS = [
  { id: 'blue', name: 'Blue', color: '#3B7DD8' },
  { id: 'violet', name: 'Violet', color: '#8B6FD4' },
  { id: 'teal', name: 'Teal', color: '#2F7E80' },
  { id: 'lime', name: 'Lime', color: '#6F8F1F' },
  { id: 'mustard', name: 'Mustard', color: '#A3741A' },
  { id: 'red', name: 'Red', color: '#C3342A' },
] as const;
type Accent = (typeof ACCENTS)[number]['id'];

const VIEWS = [
  { id: 'day', name: 'Day' },
  { id: 'week', name: 'Week' },
  { id: 'month', name: 'Month' },
  // CAL-PRO-1's fourth view. The artboard draws three segments; a
  // studio who had Agenda should not lose it to a redesign, and a
  // fourth segment in the same control is the design extended rather
  // than a second pattern introduced.
  { id: 'agenda', name: 'Agenda' },
] as const;
type View = (typeof VIEWS)[number]['id'];

const VIEW_KEY = 'orbit.calendar.view';
const PAPER_KEY = 'orbit.planner.paper';
const ACCENT_KEY = 'orbit.planner.accent';
/** Sunday, as the artboard and the old month grid both had it. */
const WEEK_STARTS_ON = 0 as const;

const read = (key: string, fallback: string): string => {
  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};
const write = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode. Not remembering is survivable.
  }
};

/* ──────────────────────────── the page ──────────────────────────── */

/**
 * Everything the screen reads, supplied from outside.
 *
 * PLANNER-4b — this is how the MARKETING page shows the calendar. It
 * used to keep a hand-written copy of the screen (`CalendarReal`),
 * which is how this repo came to have four calendars; a device that
 * mounts the real component over injected rows cannot drift from it.
 *
 * When it is set, nothing writes: no mutation fires, and the
 * Appearance control does not touch the app's own theme — a marketing
 * page is not the place to discover that clicking Dark in a laptop
 * mock-up turned the whole site dark.
 */
export interface PlannerPreviewData {
  /** Which view to open on, since there is no stored choice to read. */
  view?: View;
  events: DetailedCalendarEvent[];
  reminders: ReminderRow[];
  bookingLinks: { id: string; is_active: boolean }[];
  availabilityRules: { day_of_week: number; start_time: string; end_time: string }[];
}

export function PlannerCalendar({ preview }: { preview?: PlannerPreviewData } = {}) {
  const { mode, setMode } = useThemeMode();
  const { data: organization } = useOrganization();
  const { data: liveRules = [] } = useAvailabilityRules();
  const { data: liveLinks = [] } = useBookingLinks();
  const availabilityRules = preview?.availabilityRules ?? liveRules;
  const bookingLinks = preview?.bookingLinks ?? liveLinks;
  const { byId, events: liveEvents } = useCalendarStream();
  const allEvents = preview?.events ?? liveEvents;
  const createCalendarEvent = useCreateCalendarEvent();
  const deleteCalendarEvent = useDeleteCalendarEvent();
  const updateCalendarEvent = useUpdateCalendarEvent();
  const queryClient = useQueryClient();

  const [view, setViewState] = useState<View>(() => {
    if (preview?.view) return preview.view;
    const saved = read(VIEW_KEY, 'month');
    return (VIEWS as readonly { id: string }[]).some((v) => v.id === saved) ? (saved as View) : 'month';
  });
  // `sel` is the day in focus and `cur` the month on show. Two pieces
  // of state and not one, exactly as the artboard has it: paging the
  // month grid should not move the selected day, and picking a day in
  // March should not drag the grid back to October.
  const [sel, setSel] = useState<Date>(() => new Date());
  const [cur, setCur] = useState<Date>(() => startOfMonth(new Date()));
  const [viewingId, setViewingId] = useState<string | null>(null);

  // Dark is the app's mode; Sand is a paper over light. Picking either
  // of the first two therefore writes BOTH, so the shell follows.
  const [paper, setPaperState] = useState<Paper>(() => {
    const saved = read(PAPER_KEY, '') as Paper;
    if (saved === 'sand') return 'sand';
    return mode === 'dark' ? 'dark' : 'light';
  });
  const [accent, setAccentState] = useState<Accent>(() => {
    const saved = read(ACCENT_KEY, 'blue') as Accent;
    return ACCENTS.some((a) => a.id === saved) ? saved : 'blue';
  });

  const setPaper = (next: Paper) => {
    setPaperState(next);
    if (preview) return;
    write(PAPER_KEY, next);
    // Light and Dark ARE the app's mode — which is exactly why a
    // preview must not reach it. The paper still changes on screen.
    setMode(next === 'dark' ? 'dark' : 'light');
  };
  const setAccent = (next: Accent) => {
    setAccentState(next);
    if (!preview) write(ACCENT_KEY, next);
  };
  const setView = (next: View) => {
    setViewState(next);
    if (!preview) write(VIEW_KEY, next);
    setCur(startOfMonth(sel));
  };

  // PLANNER-2 — the Calendars list decides what is on show and what
  // colour it is. Everything below renders `events`, which is already
  // filtered; nothing downstream has to know the panel exists.
  const { visible: events, colorOf, typeRows, sourceRows } = useCalendarFilters(allEvents, cur);

  /* ───────────────────── what goes in a day ───────────────────── */

  const byDay = useMemo(() => {
    const map = new Map<string, DetailedCalendarEvent[]>();
    for (const e of events) {
      const key = format(e.startAt, 'yyyy-MM-dd');
      const list = map.get(key) ?? [];
      list.push(e);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    return map;
  }, [events]);

  const forDay = useCallback(
    (d: Date) => byDay.get(format(d, 'yyyy-MM-dd')) ?? [],
    [byDay],
  );

  /** The 42 cells of the month on show, starting on a Sunday. */
  const monthCells = useMemo(() => {
    const first = startOfWeek(startOfMonth(cur), { weekStartsOn: WEEK_STARTS_ON });
    return Array.from({ length: 42 }, (_, i) => addDays(first, i));
  }, [cur]);

  const shownDays = useMemo(
    () => (view === 'week' ? weekDays(sel, WEEK_STARTS_ON) : [sel]),
    [sel, view],
  );

  const dowNames = useMemo(
    () => monthCells.slice(0, 7).map((d) => ({ long: format(d, 'EEE'), initial: format(d, 'EEEEE') })),
    [monthCells],
  );

  /* ─────────────────────── the title line ─────────────────────── */

  const { titleMain, titleSub } = useMemo(() => {
    if (view === 'month') return { titleMain: format(cur, 'MMMM'), titleSub: format(cur, 'yyyy') };
    if (view === 'week') {
      const week = weekDays(sel, WEEK_STARTS_ON);
      const a = week[0];
      const b = week[6];
      return {
        titleMain: `${format(a, 'MMM d')} – ${format(b, 'MMM d')}`,
        titleSub: format(b, 'yyyy'),
      };
    }
    if (view === 'agenda') return { titleMain: 'Agenda', titleSub: format(new Date(), 'yyyy') };
    return { titleMain: format(sel, 'EEEE, MMMM d'), titleSub: format(sel, 'yyyy') };
  }, [cur, sel, view]);

  /** The artboard's day heading: "Today", then the count. */
  const dayHeading = useMemo(() => {
    if (isToday(sel)) return 'Today';
    const diff = Math.round((sel.getTime() - new Date().setHours(0, 0, 0, 0)) / 86400000);
    if (diff === 1) return 'Tomorrow';
    if (diff === -1) return 'Yesterday';
    return format(sel, 'EEEE');
  }, [sel]);

  const daySummary = useMemo(() => {
    const n = forDay(sel).length;
    return n === 1 ? '1 event' : `${n} events`;
  }, [forDay, sel]);

  const step = (n: number) => {
    if (view === 'month') {
      setCur((m) => addMonths(m, n));
      return;
    }
    const next = addDays(sel, view === 'week' ? 7 * n : n);
    setSel(next);
    setCur(startOfMonth(next));
  };
  const goToday = () => {
    const now = new Date();
    setSel(now);
    setCur(startOfMonth(now));
  };
  const pickDay = (d: Date) => {
    setSel(d);
    setCur(startOfMonth(d));
  };

  /* ───────────────────────── the actions ───────────────────────── */

  const viewing: DetailedCalendarEvent | null = useMemo(
    () => (viewingId ? events.find((e) => e.id === viewingId) ?? null : null),
    [events, viewingId],
  );

  /* ─────────────────────── CAL-EDIT-1 · the composer ──────────────
     The stream row carries what the GRID needs to draw a chip, which
     is not everything a block has: `client_id` in particular is only
     on the underlying row. Same query key as the stream's own, so
     react-query serves it from cache rather than fetching twice. */
  const { data: rawEvents = [] } = useCalendarEvents();
  const rawById = useMemo(() => {
    const m = new Map<string, CalendarEventRow>();
    for (const r of rawEvents) m.set(r.id, r);
    return m;
  }, [rawEvents]);

  const [composer, setComposer] = useState<ComposerDraft | null>(null);
  const [savingBlock, setSavingBlock] = useState(false);

  /* ──────────────── CAL-KEY-1 · select, then Backspace ────────────
     Rennan: *"i also need to be able to click it and delete it with
     my bacspace on my keyboard."*

     Which forces a change to what a click does. A click used to open
     the details modal outright, and you cannot press Backspace at a
     thing that is behind a modal. So: FIRST click selects, second
     click (or Enter) opens the details. That is Apple Calendar's
     behaviour and the only arrangement in which "click it and delete
     it" is a sentence that works. Details are one more click than
     they were; everything else is one fewer. */
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Moving to another day or another view means the thing the
  // keyboard was pointed at is no longer on screen; leaving it armed
  // is how you delete something you cannot see.
  useEffect(() => setSelectedId(null), [view, sel]);

  const handleEventClick = useCallback(
    (e: DetailedCalendarEvent) => {
      if (selectedId === e.id) {
        setViewingId(e.id);
        return;
      }
      setSelectedId(e.id);
    },
    [selectedId],
  );

  const deleteSelected = useCallback(async () => {
    if (!selectedId || preview) return;
    const e = events.find((x) => x.id === selectedId);
    if (!e) {
      setSelectedId(null);
      return;
    }
    const id = byId.get(e.id)?.rawId;
    if (!id) {
      // Same rule delete and drag already use: a due date belongs to
      // its project, a booking to the client who chose it, and a
      // mirrored event to the calendar it came from.
      toast.error("That one isn't deleted from the calendar");
      return;
    }
    const raw = rawById.get(id);
    try {
      await deleteCalendarEvent.mutateAsync(id);
      setSelectedId(null);
      if (viewingId === e.id) setViewingId(null);
      if (composer?.id === id) setComposer(null);
      toast.success(`Deleted “${e.title}”`, {
        // A keystroke that destroys something needs a way back, and
        // the way back is an insert: the row is gone, so Undo makes a
        // new one with the same fields and a NEW id. That is only
        // safe because the rule above means nothing deletable here
        // was ever mirrored to an external calendar — there is no
        // google_event_id to orphan.
        action: raw
          ? {
              label: 'Undo',
              onClick: () => {
                void createCalendarEvent
                  .mutateAsync({
                    title: raw.title,
                    description: raw.description,
                    start_at: raw.start_at,
                    end_at: raw.end_at,
                    all_day: raw.all_day,
                    location: raw.location,
                    project_id: raw.project_id,
                    client_id: raw.client_id ?? null,
                    color: raw.color,
                    image_url: raw.image_url,
                    meeting_url: raw.meeting_url,
                  })
                  .catch(() => toast.error('Could not put that back'));
              },
            }
          : undefined,
      });
    } catch {
      toast.error('Could not delete');
    }
  }, [
    byId,
    composer?.id,
    createCalendarEvent,
    deleteCalendarEvent,
    events,
    preview,
    rawById,
    selectedId,
    viewingId,
  ]);

  useEffect(() => {
    if (preview) return;
    const onKey = (ev: KeyboardEvent) => {
      const t = ev.target as HTMLElement | null;
      // Never while something is being typed into — Backspace in the
      // notes box is a character, not a command.
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      // A modifier means the browser or the OS is being addressed.
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      if (ev.key === 'Escape') {
        setSelectedId(null);
        return;
      }
      if (!selectedId) return;
      if (ev.key === 'Enter') {
        ev.preventDefault();
        setViewingId(selectedId);
        return;
      }
      if (ev.key === 'Backspace' || ev.key === 'Delete') {
        // Backspace is Back in a browser with no focused field, which
        // would take the studio off the calendar entirely.
        ev.preventDefault();
        void deleteSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteSelected, preview, selectedId]);

  /** Open the composer on an existing block, from the details modal. */
  const openComposer = useCallback(
    (e: DetailedCalendarEvent) => {
      const id = byId.get(e.id)?.rawId;
      const raw = id ? rawById.get(id) : undefined;
      if (!id) {
        // A due date, a milestone or a booking is edited where it
        // lives — the project, or the booking. Saying so is kinder
        // than an Edit button that writes to the wrong thing.
        toast.error("That one isn't edited from the calendar");
        return;
      }
      setViewingId(null);
      setComposer({
        id,
        title: raw?.title ?? e.title,
        description: raw?.description ?? e.description ?? '',
        startAt: e.startAt,
        endAt: e.endAt,
        allDay: !!e.allDay,
        location: raw?.location ?? e.location ?? '',
        projectId: raw?.project_id ?? e.projectId ?? null,
        clientId: raw?.client_id ?? null,
      });
    },
    [byId, rawById],
  );

  const handleComposerSave = useCallback(
    async (values: ComposerSave, id?: string) => {
      if (preview) return;
      setSavingBlock(true);
      try {
        if (id) {
          await updateCalendarEvent.mutateAsync({ id, ...values });
          toast.success('Saved');
        } else {
          if (!organization?.id) {
            toast.error('Business not ready — try again in a moment');
            return;
          }
          await createCalendarEvent.mutateAsync(values);
          toast.success('Block added');
        }
        setComposer(null);
      } catch (err) {
        toast.error((err as Error)?.message || "That didn't save");
      } finally {
        setSavingBlock(false);
      }
    },
    [createCalendarEvent, organization?.id, preview, updateCalendarEvent],
  );

  /**
   * Which events the studio may pick up. A due date or a milestone is
   * the PROJECT's date; a booking is a time a client was sent a
   * confirmation about; anything mirrored in from Google, Microsoft or
   * Apple would be put back by the next sync. What is left is the
   * studio's own blocks. (CAL-PRO-2, carried across whole.)
   */
  const canDragEvent = useCallback(
    (e: DetailedCalendarEvent) =>
      e.type === 'personal' && e.source === 'orbit' && !e.allDay && !!byId.get(e.id)?.rawId,
    [byId],
  );

  const handleEventMove = useCallback(
    async (e: DetailedCalendarEvent, next: Span) => {
      if (preview) return;
      const id = byId.get(e.id)?.rawId;
      if (!id) return;
      const start_at = next.start.toISOString();
      const end_at = next.end.toISOString();
      const filter = { queryKey: ['calendar-events'] } as const;
      const before = queryClient.getQueriesData<CalendarEventRow[]>(filter);
      // Painted before it is saved; put back, loudly, if the write
      // fails. Without this the event springs back for a few hundred
      // milliseconds, which reads as the drag having failed.
      queryClient.setQueriesData<CalendarEventRow[]>(filter, (old) =>
        old?.map((r) => (r.id === id ? { ...r, start_at, end_at } : r)),
      );
      try {
        await updateCalendarEvent.mutateAsync({ id, start_at, end_at });
      } catch (err) {
        before.forEach(([key, data]) => queryClient.setQueryData(key, data));
        toast.error((err as Error)?.message || "That didn't save — it's back where it was");
      }
    },
    [byId, preview, queryClient, updateCalendarEvent],
  );

  const handleDelete = async (e: DetailedCalendarEvent) => {
    if (preview) return;
    const id = byId.get(e.id)?.rawId;
    if (!id) {
      toast.error("Can't delete this one from here");
      return;
    }
    try {
      await deleteCalendarEvent.mutateAsync(id);
      toast.success('Deleted');
      setViewingId(null);
    } catch {
      toast.error('Could not delete');
    }
  };

  /**
   * A block drawn on the grid, or an hour tapped.
   *
   * CAL-EDIT-1 — this used to INSERT immediately with the title
   * hard-coded to 'New block'. Every abandoned drag left one behind,
   * and on a week grid abandoning a drag is routine: you misjudge the
   * column, let go early, change your mind. Now the drag yields a
   * span, the span opens the composer, and the row appears when the
   * studio has said what it is.
   */
  const addBlock = useCallback(
    (span: Span) => {
      if (preview) return;
      if (!organization?.id) {
        toast.error('Business not ready — try again in a moment');
        return;
      }
      setComposer({
        title: '',
        description: '',
        startAt: span.start,
        endAt: span.end,
        allDay: false,
        location: '',
        projectId: null,
        clientId: null,
      });
    },
    [organization?.id, preview],
  );

  /**
   * The hours a client can book, for the grid to dim the outside of.
   * Null when no hours are set at all — shading every column end to
   * end reads as broken rather than as open.
   */
  const bookableHours = useCallback(
    (day: Date) => (availabilityRules.length ? bookableBands(availabilityRules, day) : null),
    [availabilityRules],
  );

  /* ──────────────────────────── render ──────────────────────────── */

  return (
    <div
      className={cn('plan-app', `plan-theme-${paper}`, `plan-accent-${accent}`)}
      style={{
        minHeight: '100vh',
        boxSizing: 'border-box',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: 20,
        padding: 20,
      }}
    >
      {/* ── left rail ────────────────────────────────────────── */}
      <aside
        style={{ flex: '1 1 240px', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px 0' }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'var(--accent)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="#fff"
              strokeWidth="1.6"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <rect x="2" y="3" width="12" height="11" rx="2" />
              <path d="M2 7h12M5 1.5v3M11 1.5v3" />
            </svg>
          </div>
          <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>Planner</div>
        </div>

        {/* Mini month. Its own month, independent of the big grid's,
            so it can be used to go and look somewhere. */}
        <section className="plan-panel" style={{ padding: '14px 12px 12px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 2px 10px 6px',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>{format(cur, 'MMMM yyyy')}</div>
            <div style={{ display: 'flex', gap: 2 }}>
              <button
                type="button"
                className="plan-mini"
                aria-label="Previous month"
                onClick={() => setCur((m) => addMonths(m, -1))}
              >
                <Chevron dir="left" size={14} />
              </button>
              <button
                type="button"
                className="plan-mini"
                aria-label="Next month"
                onClick={() => setCur((m) => addMonths(m, 1))}
              >
                <Chevron dir="right" size={14} />
              </button>
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              justifyItems: 'center',
              rowGap: 2,
            }}
          >
            {dowNames.map((d, i) => (
              <div
                key={i}
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'var(--muted)',
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {d.initial}
              </div>
            ))}
            {monthCells.map((d) => (
              <button
                key={d.toISOString()}
                type="button"
                className={cn(
                  'plan-mini plan-tnum',
                  !isSameMonth(d, cur) && 'dim',
                  isToday(d) && 'today',
                  isSameDay(d, sel) && !isToday(d) && 'sel',
                )}
                onClick={() => pickDay(d)}
                aria-label={format(d, 'EEEE, MMMM d')}
              >
                {format(d, 'd')}
              </button>
            ))}
          </div>
        </section>

        <CalendarsPanel month={format(cur, 'MMMM')} types={typeRows} sources={sourceRows} />

        {/* Appearance. The artboard's panel, bound to the app: Light
            and Dark set Orbit's own mode, so the shell follows. */}
        <section className="plan-panel" style={{ padding: '14px 12px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, padding: '0 6px 10px' }}>Appearance</div>
          <div
            style={{
              display: 'flex',
              background: 'var(--track)',
              borderRadius: 10,
              padding: 2,
              gap: 2,
            }}
          >
            {PAPERS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={cn('plan-seg', paper === p.id && 'on')}
                style={{ flex: 1, padding: 0 }}
                aria-pressed={paper === p.id}
                onClick={() => setPaper(p.id)}
              >
                {p.name}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', padding: '14px 6px 8px' }}>Accent</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 6px 4px' }}>
            {ACCENTS.map((a) => (
              <button
                key={a.id}
                type="button"
                className={cn('plan-sw', accent === a.id && 'on')}
                style={{ background: a.color }}
                aria-label={a.name}
                aria-pressed={accent === a.id}
                onClick={() => setAccent(a.id)}
              />
            ))}
          </div>
        </section>
      </aside>

      {/* ── main column ──────────────────────────────────────── */}
      <main
        className="plan-main"
        style={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            padding: '4px 4px 0',
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 26,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: '36px',
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {titleMain} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>{titleSub}</span>
          </h1>
          <div
            style={{ display: 'flex', background: 'var(--track)', borderRadius: 10, padding: 2, gap: 2 }}
          >
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                className={cn('plan-seg', view === v.id && 'on')}
                aria-pressed={view === v.id}
                onClick={() => setView(v.id)}
              >
                {v.name}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              className="plan-btn plan-icon"
              aria-label={view === 'month' ? 'Previous month' : view === 'week' ? 'Previous week' : 'Previous day'}
              onClick={() => step(-1)}
              disabled={view === 'agenda'}
              style={view === 'agenda' ? { opacity: 0.4, cursor: 'default' } : undefined}
            >
              <Chevron dir="left" size={16} />
            </button>
            <button type="button" className="plan-btn" onClick={goToday}>
              Today
            </button>
            <button
              type="button"
              className="plan-btn plan-icon"
              aria-label={view === 'month' ? 'Next month' : view === 'week' ? 'Next week' : 'Next day'}
              onClick={() => step(1)}
              disabled={view === 'agenda'}
              style={view === 'agenda' ? { opacity: 0.4, cursor: 'default' } : undefined}
            >
              <Chevron dir="right" size={16} />
            </button>
          </div>
        </header>

        {view === 'month' && (
          <section className="plan-panel" style={{ overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
              {dowNames.map((d, i) => (
                <div
                  key={i}
                  style={{
                    padding: '10px 10px 8px',
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--muted)',
                  }}
                >
                  {d.long}
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gridAutoRows: 'minmax(104px, auto)',
              }}
            >
              {monthCells.map((d) => {
                const list = forDay(d);
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    className={cn(
                      'plan-cell',
                      !isSameMonth(d, cur) && 'dim',
                      isToday(d) && 'today',
                      isSameDay(d, sel) && 'sel',
                    )}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 4,
                      padding: 6,
                      minWidth: 0,
                    }}
                    onClick={() => pickDay(d)}
                    aria-label={format(d, 'EEEE, MMMM d')}
                  >
                    <span className="plan-num plan-tnum">{format(d, 'd')}</span>
                    {/* The chips get a wrapper so a phone can lay them
                        out as a row of dots instead of a stack of
                        truncated first letters — see planner.css. */}
                    <span className="plan-chips">
                      {list.slice(0, 3).map((e) => (
                        <span key={e.id} className="plan-chip">
                          <span className="plan-dot" style={{ background: colorOf(e) }} />
                          <span className="t">{e.title}</span>
                        </span>
                      ))}
                      {list.length > 3 && (
                        <span className="plan-more">
                          +{list.length - 3}
                          <span className="plan-more-word"> more</span>
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {view === 'agenda' && (
          <AgendaView
            events={events}
            colorOf={colorOf}
            selectedId={selectedId}
            onEventClick={handleEventClick}
          />
        )}

        {(view === 'week' || view === 'day') && (
          // PLANNER-1b restyles this to the artboard's own grid — 48px
          // hours, a 56px gutter, filled blocks. The engine underneath
          // it already does what the artboard cannot: split a column
          // when two things collide, and let you drag.
          <TimeGridView
            days={shownDays}
            events={events}
            colorOf={colorOf}
            onEventClick={handleEventClick}
            selectedId={selectedId}
            onPickDay={(d) => {
              setSel(d);
              setView('day');
            }}
            onAddAtTime={(at) => addBlock({ start: at, end: new Date(at.getTime() + 60 * 60_000) })}
            onEventMove={handleEventMove}
            onCreateRange={addBlock}
            canDragEvent={canDragEvent}
            bookableHours={bookableHours}
            dayLabel={dayHeading}
            daySummary={daySummary}
          />
        )}
        <SchedulerSection links={bookingLinks} rules={availabilityRules} />
      </main>

      {/* ── right rail ───────────────────────────────────────── */}
      <aside
        className="plan-rail-right"
        style={{ flex: '1 1 280px', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        {/* Above the reminders, not over the grid: naming a block you
            just drew is something you do while still looking at the
            hours around it. */}
        <EventComposer
          draft={composer}
          saving={savingBlock}
          onCancel={() => setComposer(null)}
          onSave={handleComposerSave}
          onDelete={async (id) => {
            try {
              await deleteCalendarEvent.mutateAsync(id);
              toast.success('Deleted');
              setComposer(null);
            } catch {
              toast.error('Could not delete');
            }
          }}
        />
        <RemindersPanel selected={sel} rows={preview?.reminders} />
      </aside>

      <EventDetailsModal
        event={viewing}
        onClose={() => setViewingId(null)}
        /* CAL-EDIT-1 — `onEdit` has been a prop on this modal since
           #351 and has never been passed, so the Edit button has
           never rendered and no field of a block could be changed.
           This is that button. */
        onEdit={openComposer}
        onDelete={handleDelete}
      />
    </div>
  );
}

/**
 * SchedulerSection — booking links and availability, on the calendar.
 *
 * PLANNER-4. Rennan: *"merge the existing settings into this new
 * calendar."* The cream calendar page had this all along — it embedded
 * the whole `SchedulingSettings` panel below the month grid so a
 * studio could author a /book/:slug without going to Settings — and
 * every redesign since has dropped it. This is the third calendar to
 * carry it and the first to keep it.
 *
 * It mounts the SAME component Settings → Scheduling renders. A second
 * booking-link editor built for the calendar would be a second one to
 * keep in step, and they would not stay in step.
 *
 * The panel inside still wears Settings' own appearance rather than
 * the planner's: it is shared with that page, so restyling it here
 * would restyle it there. The disclosure around it is the planner's.
 *
 * Shut by default, and it remembers. A calendar is for looking at a
 * week; the scheduler is for the ten minutes a month you change your
 * hours. The summary line answers the question without being opened.
 */
const SCHEDULER_KEY = 'orbit.planner.scheduler';

function SchedulerSection({
  links,
  rules,
}: {
  links: { id: string; is_active: boolean }[];
  rules: { day_of_week: number }[];
}) {
  const [open, setOpen] = useState(() => read(SCHEDULER_KEY, '0') === '1');
  const toggle = () => {
    const next = !open;
    setOpen(next);
    write(SCHEDULER_KEY, next ? '1' : '0');
  };

  const live = links.filter((l) => l.is_active).length;
  // Distinct weekdays, not rules: a studio with a morning and an
  // afternoon rule on a Tuesday is open one day, not two.
  const openDays = new Set(rules.map((r) => r.day_of_week)).size;
  const summary = [
    live === 1 ? '1 live link' : `${live} live links`,
    openDays === 0 ? 'no hours set' : openDays === 1 ? '1 day open' : `${openDays} days open`,
  ].join(' · ');

  return (
    <section className="plan-panel">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '14px 16px',
        }}
      >
        <span style={{ minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: 'var(--muted)',
            }}
          >
            Scheduler
          </span>
          <span className="plan-truncate" style={{ marginTop: 2, fontSize: 14, fontWeight: 600 }}>
            Booking links &amp; availability
          </span>
        </span>
        <span style={{ display: 'flex', flex: 'none', alignItems: 'center', gap: 12 }}>
          <span className="plan-tnum plan-hide-sm" style={{ fontSize: 12, color: 'var(--muted)' }}>
            {summary}
          </span>
          <span
            aria-hidden
            style={{
              display: 'inline-flex',
              transition: 'transform .15s',
              transform: open ? 'rotate(90deg)' : undefined,
              color: 'var(--muted)',
            }}
          >
            <Chevron dir="right" size={16} />
          </span>
        </span>
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--line)', padding: 16 }}>
          <SchedulingSettings />
        </div>
      )}
    </section>
  );
}

/**
 * The Calendars panel. Two groups: what a thing IS, and where it came
 * FROM. The second has no colour dots on purpose — a source is a
 * filter, not a hue, and dots on both would imply you could colour by
 * two things at once.
 */
function CalendarsPanel({
  month,
  types,
  sources,
}: {
  month: string;
  types: CalendarRow[];
  sources: CalendarRow[];
}) {
  return (
    <section className="plan-panel" style={{ padding: '14px 12px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 6px 8px',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600 }}>Calendars</div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{month}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {types.map((r) => (
          <CalendarRowView key={r.id} row={r} />
        ))}
        {sources.length > 1 && (
          <>
            <div className="plan-ghead">Connected</div>
            {sources.map((r) => (
              <CalendarRowView key={r.id} row={r} />
            ))}
          </>
        )}
      </div>
    </section>
  );
}

function CalendarRowView({ row }: { row: CalendarRow }) {
  const id = `plan-cal-${row.id}`;
  return (
    <div className="plan-row" style={{ padding: 6 }}>
      <input
        type="checkbox"
        className="plan-box"
        id={id}
        checked={row.on}
        onChange={row.toggle}
        style={
          row.color
            ? { background: row.on ? row.color : 'transparent', borderColor: row.color }
            : undefined
        }
      />
      <label htmlFor={id} style={{ flex: 1, fontSize: 13, cursor: 'pointer', minWidth: 0 }}>
        {row.name}
      </label>
      <span className="plan-tnum" style={{ fontSize: 12, color: 'var(--muted)' }}>
        {row.count}
      </span>
      {row.cycle && (
        <button
          type="button"
          className="plan-dot"
          style={{ width: 12, height: 12, background: row.color }}
          aria-label={`Change the colour of ${row.name}`}
          onClick={row.cycle}
        />
      )}
    </div>
  );
}

function Chevron({ dir, size }: { dir: 'left' | 'right'; size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={dir === 'left' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'} />
    </svg>
  );
}

export default PlannerCalendar;
